/**
 * Google Takeout date restore — UI layer.
 *
 * The whole job in one line: read the date out of each photo's `.json` sidecar
 * and write it into the picture's own EXIF, so a library imported anywhere else
 * sorts by when the shot was taken rather than by when it was exported.
 *
 * Writing is delegated to `window.ImageMeta` (image-metadata-engine.js), which
 * splices the EXIF segment and leaves the pixels byte-identical. Nothing here
 * ever touches a canvas: re-encoding a whole library to fix a timestamp would
 * be a silent quality downgrade of every photo in it.
 */

import { matchAll, parseSidecar, exifDate } from './sidecar-match.js';

const T = (k, p) => {
  let s = (window.__TD_I18N && window.__TD_I18N[k]) || k;
  if (p) for (const [a, b] of Object.entries(p)) s = s.replace(new RegExp(':' + a, 'g'), b);
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/** Formats ImageMeta can write EXIF into losslessly. */
const WRITABLE = /\.(jpe?g)$/i;
const MEDIA = /\.(jpe?g|png|gif|webp|heic|heif|avif|tiff?|mp4|mov|m4v|3gp)$/i;

let picked = [];
let result = null;

function summarise(files) {
  const media = [], json = [];
  for (const f of files) {
    const n = f.name;
    if (/\.json$/i.test(n)) json.push(n);
    else if (MEDIA.test(n)) media.push(n);
  }
  return { media, json };
}

async function analyse(files) {
  const out = el('td-out');
  out.innerHTML = `<div class="td-working">${T('working')}</div>`;
  out.hidden = false;
  window.__stage && window.__stage('analyzing');

  const byName = new Map(files.map((f) => [f.name, f]));
  const { media, json } = summarise(files);
  if (!media.length) {
    out.innerHTML = card('warn', T('verdict_no_media'), T('body_no_media'));
    return;
  }
  if (!json.length) {
    out.innerHTML = card('warn', T('verdict_no_json'), T('body_no_json'));
    return;
  }

  const m = matchAll(media, json);

  // Read every matched sidecar once, even when several photos share it.
  const sidecars = new Map();
  for (const pair of m.matched) {
    if (sidecars.has(pair.json)) continue;
    const f = byName.get(pair.json);
    sidecars.set(pair.json, f ? parseSidecar(await f.text()) : null);
  }

  const plan = m.matched.map((pair) => {
    const data = sidecars.get(pair.json);
    return {
      ...pair,
      takenAt: data && data.takenAt,
      geo: data && data.latitude != null ? data : null,
      writable: WRITABLE.test(pair.media),
    };
  });

  const datable = plan.filter((p) => p.takenAt && p.writable);
  picked = { files: byName, plan, unmatched: m.unmatched, orphanJson: m.orphanJson, datable };

  out.innerHTML = [
    renderPlan(media, json, plan, m, datable),
    renderLists(m, plan),
  ].join('');
  wire();
  window.__stage && window.__stage('planned');
}

const card = (tone, title, body, extra = '') =>
  `<div class="td-card td-tone-${tone}"><p class="td-title">${title}</p>
     <p class="td-body">${body}</p>${extra}</div>`;

function renderPlan(media, json, plan, m, datable) {
  const noDate = plan.filter((p) => !p.takenAt).length;
  const notWritable = plan.filter((p) => p.takenAt && !p.writable).length;
  const tone = datable.length ? 'ok' : 'warn';

  const rows = [
    [T('stat_media'), String(media.length)],
    [T('stat_sidecars'), String(json.length)],
    [T('stat_matched'), String(m.matched.length)],
    [T('stat_datable'), String(datable.length)],
    m.unmatched.length ? [T('stat_unmatched'), String(m.unmatched.length)] : null,
    noDate ? [T('stat_no_date'), String(noDate)] : null,
    notWritable ? [T('stat_not_writable'), String(notWritable)] : null,
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('');

  const action = datable.length
    ? `<div class="td-actions">
         <button type="button" class="td-btn" id="td-run">${T('run', { n: String(datable.length) })}</button>
       </div>`
    : '';

  return `${card(tone, T('verdict_ready'), T('body_ready', { n: String(datable.length) }), action)}
    <section class="td-block"><h3>${T('stats_title')}</h3><table class="td-stats">${rows}</table></section>`;
}

/** Naming the weak matches is the honest half: a guess shown as a guess. */
function renderLists(m, plan) {
  const weak = plan.filter((p) => p.rule !== 'exact' && p.rule !== 'supplemental');
  let html = '';
  if (weak.length) {
    const items = weak.slice(0, 200).map((p) =>
      `<li><code>${esc(p.media)}</code> → <code>${esc(p.json)}</code> <span class="td-rule">${T('rule_' + p.rule)}</span></li>`).join('');
    html += `<section class="td-block"><h3>${T('weak_title', { n: String(weak.length) })}</h3>
        <p class="td-body">${T('weak_desc')}</p><ul class="td-list">${items}</ul></section>`;
  }
  if (m.unmatched.length) {
    const items = m.unmatched.slice(0, 200).map((n) => `<li><code>${esc(n)}</code></li>`).join('');
    html += `<section class="td-block"><h3>${T('unmatched_title', { n: String(m.unmatched.length) })}</h3>
        <p class="td-body">${T('unmatched_desc')}</p><ul class="td-list">${items}</ul></section>`;
  }
  return html;
}

async function run() {
  const { files, datable } = picked;
  const btn = el('td-run');
  if (btn) { btn.disabled = true; btn.textContent = T('running'); }

  /* One run per FILE, not per click: the cap has to mean the same thing whether
   * someone drops one photo or a whole export. */
  const keys = window.__toolLimit && window.__toolLimit.fileKeys
    ? window.__toolLimit.fileKeys(datable.map((d) => files.get(d.media)).filter(Boolean))
    : null;
  const _run = window.__toolLimit ? window.__toolLimit.begin('takeout-dates', keys) : null;
  if (window.__toolLimit && !_run) { if (btn) { btn.disabled = false; btn.textContent = T('run', { n: String(datable.length) }); } return; }

  const done = [], failed = [];
  for (const item of datable) {
    const file = files.get(item.media);
    if (!file) { failed.push({ name: item.media, why: 'missing' }); continue; }
    try {
      const stamp = exifDate(item.takenAt, new Date().getTimezoneOffset());
      /* ImageMeta takes its field writes under `set`. Passing them flat inserts
       * an EXIF block containing nothing — the file grew by 24 bytes and not one
       * date was written, which every structural check happily accepted. */
      const blob = await window.ImageMeta.edit(file, {
        set: { dateOriginal: stamp, dateDigitized: stamp, datetime: stamp },
      });
      done.push({ name: item.media, blob, takenAt: item.takenAt });
    } catch (err) {
      failed.push({ name: item.media, why: (err && err.message) || 'error' });
    }
    if (btn) btn.textContent = T('running_n', { done: String(done.length), total: String(datable.length) });
  }

  /* Per-file failures are collected for the report but were never reported to
   * US. "Wrote 3 of 40" is the shape of a real bug, and it looked exactly like
   * a small run in the dashboard. */
  if (failed.length) {
    window.__aeDiagnostic && window.__aeDiagnostic({
      issue: 'write_failed',
      failed: failed.length, written: done.length,
      reasons: [...new Set(failed.map((f) => String(f.why).slice(0, 40)))].slice(0, 3).join(','),
    });
  }

  if (!done.length) { _run && _run.abort(); }
  else { _run && _run.commit(); }

  result = { done, failed };
  el('td-out').insertAdjacentHTML('afterbegin', renderResult(done, failed));
  const save = el('td-save');
  if (save) save.addEventListener('click', saveZip);
  window.__aeToolComplete && window.__aeToolComplete({
    written: done.length, failed: failed.length, planned: datable.length });
}

function renderResult(done, failed) {
  const tone = done.length ? 'ok' : 'bad';
  const extra = done.length
    ? `<div class="td-actions"><button type="button" class="td-btn" id="td-save">${T('save_zip')}</button></div>`
    : '';
  const failList = failed.length
    ? `<ul class="td-list">${failed.slice(0, 50).map((f) =>
        `<li><code>${esc(f.name)}</code> — ${esc(f.why)}</li>`).join('')}</ul>`
    : '';
  return card(tone, done.length ? T('verdict_done') : T('verdict_none_written'),
    T('body_done', { n: String(done.length), failed: String(failed.length) }), extra + failList);
}

async function saveZip() {
  const { buildZip } = await import('../archive/archive-repair-core.js');
  const items = [];
  for (const d of result.done) {
    items.push({ path: d.name, bytes: new Uint8Array(await d.blob.arrayBuffer()), mtime: d.takenAt * 1000 });
  }
  const bytes = await buildZip(items, {});
  window.__downloadFile(new Blob([bytes], { type: 'application/zip' }), 'photos-with-dates.zip');
  window.__ae && window.__ae('feature_use', { slug: 'takeout-dates', feature: 'saved_zip' });
}

function wire() {
  const r = el('td-run');
  if (r) r.addEventListener('click', run);
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('tdDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (e) => {
    const list = (e.detail && e.detail.files) || (e.detail && e.detail.file ? [e.detail.file] : []);
    if (list.length) analyse([...list]);
  });
});

export { analyse };
